// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
//
// The open findings in "All pins" view (warhol spec dave.savostyanov/0090):
// the ones to fix before print first, under their own heading, then the minor
// and check findings in a band she can fold away. Presentational: which band a
// finding sits in is `isMinorFinding`, whether the band is open is the list's.
import { CaretDown } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { isMinorFinding } from "./pinGroups";
import { type CommentWithOptionalTags } from "./reviewCommentTags";

const BAND_HEADING =
  "mb-1 mt-1 flex w-full items-center gap-2 rounded-md px-1 py-1 text-left";

export function AskV2ReviewSeverityBands({
  items,
  minorOpen,
  onToggleMinor,
  renderComment,
}: {
  items: readonly CommentWithOptionalTags[];
  minorOpen: boolean;
  onToggleMinor: () => void;
  renderComment: (comment: CommentWithOptionalTags) => ReactNode;
}) {
  const { t } = useTranslation("ui");
  const urgent = items.filter((c) => !isMinorFinding(c));
  const minor = items.filter(isMinorFinding);
  const minorListId = "ask-v2-review-band-minor-list";
  return (
    <>
      {urgent.length > 0 ? (
        <li data-testid="ask-v2-review-band-urgent">
          <h4 className={`m-0 ${BAND_HEADING}`}>
            <span
              className="size-2 shrink-0 rounded-full bg-puntt-accent-9"
              aria-hidden="true"
            />
            <Text
              as="span"
              size="1"
              weight="medium"
              className="text-puntt-accent-11"
            >
              {t("askV2.review.bands.urgent")}
            </Text>
            <span
              className="text-[12px] tabular-nums text-puntt-neutral-gray-10"
              data-testid="ask-v2-review-band-urgent-count"
            >
              {urgent.length}
            </span>
          </h4>
          <ul data-testid="ask-v2-review-band-urgent-list">
            {urgent.map(renderComment)}
          </ul>
        </li>
      ) : null}
      {minor.length > 0 ? (
        <li data-testid="ask-v2-review-band-minor">
          <h4 className="m-0">
            <button
              type="button"
              className={`${BAND_HEADING} hover:bg-puntt-neutral-gray-2`}
              data-testid="ask-v2-review-band-minor-toggle"
              aria-expanded={minorOpen}
              // why: a closed band's list is not rendered, and aria-controls must name an element that exists.
              aria-controls={minorOpen ? minorListId : undefined}
              onClick={onToggleMinor}
            >
              <span
                className="size-2 shrink-0 rounded-full border border-puntt-accent-7 bg-puntt-accent-4"
                aria-hidden="true"
              />
              <Text
                as="span"
                size="1"
                weight="medium"
                className="text-puntt-neutral-grayA-11"
              >
                {t("askV2.review.bands.minor")}
              </Text>
              <span
                className="text-[12px] tabular-nums text-puntt-neutral-gray-10"
                data-testid="ask-v2-review-band-minor-count"
              >
                {minor.length}
              </span>
              <CaretDown
                size={14}
                aria-hidden="true"
                className={`ml-auto shrink-0 text-puntt-neutral-gray-11 transition-transform ${minorOpen ? "" : "-rotate-90"}`}
              />
            </button>
          </h4>
          {minorOpen ? (
            <ul id={minorListId} data-testid={minorListId}>
              {minor.map(renderComment)}
            </ul>
          ) : null}
        </li>
      ) : null}
    </>
  );
}
